import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { PlayerStage } from "@/components/PlayerStage";
import { Queue } from "@/components/Queue";
import { LiveChat } from "@/components/LiveChat";
import { SubmitDialog } from "@/components/SubmitDialog";

export default function Show() {
  const { user } = useAuth();
  const [nowPlaying, setNowPlaying] = useState(null);
  const [queue, setQueue] = useState([]);
  const [advanceAt, setAdvanceAt] = useState(null);
  const [reviewSeconds, setReviewSeconds] = useState(60);
  const [clockOffset, setClockOffset] = useState(0);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const pollingPayment = useRef(false);

  const loadQueue = useCallback(async () => {
    try {
      const res = await api.get("/queue");
      setNowPlaying(res.data.now_playing);
      setQueue(res.data.queue);
      setAdvanceAt(res.data.advance_at);
      if (res.data.review_seconds) setReviewSeconds(res.data.review_seconds);
      if (res.data.server_time) setClockOffset(Date.now() - new Date(res.data.server_time).getTime());
    } catch (e) {
      console.error("Failed to load queue", e);
    }
  }, []);

  useEffect(() => {
    loadQueue();
    const id = setInterval(loadQueue, 3000);
    return () => clearInterval(id);
  }, [loadQueue]);

  // Payment return handling
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId || pollingPayment.current) return;
    pollingPayment.current = true;

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          toast.success("Payment confirmed — your track skipped the line! ⚡");
          setSearchParams({});
          loadQueue();
          return;
        }
        if (res.data.status === "expired" || attempts >= 6) {
          toast.error("Payment not completed.");
          setSearchParams({});
          return;
        }
      } catch {
        if (attempts >= 6) { setSearchParams({}); return; }
      }
      setTimeout(poll, 2000);
    };
    toast.info("Confirming your payment…");
    poll();
    // eslint-disable-next-line
  }, [searchParams]);

  const handleNext = async (fromTrackId) => {
    try {
      const res = await api.post("/queue/next", { from_track_id: fromTrackId });
      setNowPlaying(res.data.now_playing);
      setQueue(res.data.queue);
      setAdvanceAt(res.data.advance_at);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not advance queue");
    }
  };

  const onSubmitted = () => { loadQueue(); setRefreshKey((k) => k + 1); };

  return (
    <div className="min-h-screen bg-[#050505] grain">
      <Navbar onSubmit={user ? () => setSubmitOpen(true) : undefined} />

      <main className="max-w-[1600px] mx-auto px-5 md:px-10 py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 space-y-6">
            <PlayerStage
              track={nowPlaying}
              onNext={handleNext}
              refreshKey={refreshKey}
              advanceAt={advanceAt}
              reviewSeconds={reviewSeconds}
              clockOffset={clockOffset}
            />
            <Queue items={queue} onRefresh={loadQueue} />
          </div>
          <div className="lg:col-span-4">
            <LiveChat />
          </div>
        </div>
      </main>

      <SubmitDialog open={submitOpen} onOpenChange={setSubmitOpen} onSubmitted={onSubmitted} />
    </div>
  );
}
